// src/pages/Admin/Dashboard/Charts/PagesVisitesMiniPanel.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { countVisitesByPageToday } from "../../../../services/visitesServices";
import { FiRefreshCw, FiSearch } from "react-icons/fi";

const cx = (...c) => c.filter(Boolean).join(" ");
const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// (optionnel) labels courts si tu veux une lecture encore plus “humaine”
const PAGE_LABELS = {
  "/": "Accueil",
  "/tarifs": "Tarifs",
  "/applications": "Applications",
  "/contact": "Contact",
  "/demo": "Démo",
};

function shortLabel(path) {
  return PAGE_LABELS[path] || path || "/";
}

function StatChip({ label, value }) {
  return (
    <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80">
      <span className="opacity-80">{label}</span>{" "}
      <span className="font-semibold text-white">{value}</span>
    </div>
  );
}

export default function PagesVisitesMiniPanel({
  pollMs = 10000,
  initialLimit = 8,
}) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(initialLimit);
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  // pour afficher des +delta depuis le dernier refresh
  const prevRef = useRef(new Map()); // Map(page -> count)

  const refresh = useCallback(async () => {
    try {
      setLoading(true);

      const data = await countVisitesByPageToday();
      const formatted = (data || [])
        .map((it) => ({
          page: it.page,
          count: safeNumber(it.total_visits),
        }))
        .sort((a, b) => b.count - a.count);

      const prev = prevRef.current;
      const nextPrev = new Map();

      const withDelta = formatted.map((r) => {
        const prevCount = safeNumber(prev.get(r.page));
        const delta = r.count - prevCount;
        nextPrev.set(r.page, r.count);
        return { ...r, delta };
      });

      prevRef.current = nextPrev;
      setRows(withDelta);
      setLastSync(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs]);

  const totalToday = useMemo(
    () => rows.reduce((acc, r) => acc + safeNumber(r.count), 0),
    [rows]
  );

  const maxCount = useMemo(() => {
    const m = rows.length ? Math.max(...rows.map((r) => r.count)) : 0;
    return m || 1;
  }, [rows]);

  const filtered = useMemo(() => {
    const qq = (q || "").trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter((r) => {
      const lbl = shortLabel(r.page).toLowerCase();
      return r.page.toLowerCase().includes(qq) || lbl.includes(qq);
    });
  }, [rows, q]);

  const visible = useMemo(() => filtered.slice(0, limit), [filtered, limit]);

  const showMore = filtered.length > limit;
  const canReduce = limit > initialLimit;

  return (
    <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b1220] via-[#0b1220] to-black p-4 text-white shadow-xl">
      {/* Header minimal */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="text-xs text-white/60">Visites • aujourd’hui</div>
          <div className="text-lg font-extrabold tracking-tight">
            Pages les plus consultées
          </div>
          <div className="mt-1 text-xs text-white/50">
            {lastSync
              ? `Dernière mise à jour : ${lastSync.toLocaleTimeString("fr-FR")}`
              : "Mise à jour..."}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatChip label="Total" value={totalToday} />
          <StatChip label="Pages" value={rows.length} />

          <button
            type="button"
            onClick={refresh}
            className={cx(
              "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold",
              "border border-white/10 bg-white/5 hover:bg-white/10 transition"
            )}
            title="Rafraîchir"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
            {loading ? "Maj..." : "Rafraîchir"}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mt-4 flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
        <FiSearch className="opacity-70" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filtrer : /tarifs, accueil, contact..."
          className="w-full bg-transparent text-sm outline-none placeholder:text-white/35"
        />
      </div>

      {/* List */}
      <div className="mt-3 space-y-2">
        {visible.length ? (
          visible.map((r, idx) => {
            const pct = Math.round((r.count / maxCount) * 100);
            const label = shortLabel(r.page);

            return (
              <div
                key={r.page}
                className={cx(
                  "rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-3",
                  "hover:bg-white/[0.07] transition"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div
                        className={cx(
                          "text-xs rounded-full px-2 py-0.5 border",
                          idx === 0
                            ? "border-orange-400/30 bg-orange-400/10 text-orange-200"
                            : idx === 1
                            ? "border-yellow-400/25 bg-yellow-400/10 text-yellow-100"
                            : "border-white/10 bg-white/5 text-white/70"
                        )}
                      >
                        #{idx + 1}
                      </div>

                      <div className="truncate font-semibold text-white">
                        {label}
                      </div>
                    </div>

                    {/* path discret */}
                    {PAGE_LABELS[r.page] && (
                      <div className="mt-0.5 truncate text-xs text-white/45">
                        {r.page}
                      </div>
                    )}

                    {/* barre */}
                    <div className="mt-2 h-2 w-full rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-yellow-300/90 to-orange-500/90"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>

                  <div className="shrink-0 text-right">
                    <div className="text-lg font-extrabold leading-none">
                      {r.count}
                    </div>
                    <div className="mt-1 flex items-center justify-end gap-2">
                      <div className="text-xs text-white/45">{pct}%</div>

                      {r.delta > 0 ? (
                        <div className="text-xs font-semibold rounded-full px-2 py-0.5 border border-emerald-400/25 bg-emerald-400/10 text-emerald-200">
                          +{r.delta}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
            Aucune page trouvée.
          </div>
        )}
      </div>

      {/* Footer actions minimal */}
      {(showMore || canReduce) && (
        <div className="mt-3 flex items-center justify-center gap-2">
          {showMore && (
            <button
              type="button"
              onClick={() => setLimit((v) => v + 6)}
              className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 transition"
            >
              Afficher plus
            </button>
          )}
          {canReduce && (
            <button
              type="button"
              onClick={() => setLimit(initialLimit)}
              className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 transition"
            >
              Réduire
            </button>
          )}
        </div>
      )}
    </div>
  );
}
